import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Loader2 } from 'lucide-react'
import { usersService } from '@/services/users'
import { organizationsService } from '@/services/organizations'
import { authService } from '@/services/auth'
import { getApiErrorMessage } from '@/utils/errors'
import { CUSTOMER_TYPE, PAYMENT_TYPE, IndividualType } from '@/constants/account'
import { ROUTES } from '@/constants/routes'

const isExternalQuoteFlow = (customerType: string | null, paymentType: string | null): boolean =>
  customerType === CUSTOMER_TYPE.ORGANIZATION && paymentType === PAYMENT_TYPE.EXTERNAL

const isAdherentVerification = (
  customerType: string | null,
  individualType: string | null
): boolean => customerType === CUSTOMER_TYPE.USER && individualType === IndividualType.ADHERENT

const isCustomOfferVerificationFlow = (customPriceParam: boolean): boolean => customPriceParam

let lastVerifiedKey = ''

export function VerifyEmailPage() {
  const { t } = useTranslation('wizard')
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const email = searchParams.get('email')
  const code = searchParams.get('code')
  const customerType = searchParams.get('customer_type')
  const paymentType = searchParams.get('payment_type')
  const individualType = searchParams.get('individual_type')
  const customPriceParam = searchParams.get('custom_price') === 'true'
  const isAdherentFlow = isAdherentVerification(customerType, individualType)
  const isCustomOfferFlow = isCustomOfferVerificationFlow(customPriceParam)

  useEffect(() => {
    const key = `${email ?? ''}-${code ?? ''}-${isCustomOfferFlow}`
    if (key === lastVerifiedKey) return
    lastVerifiedKey = key

    const verifyEmail = async () => {
      if (!email || !code) {
        setError(t('emailVerification.errors.generic'))
        setIsLoading(false)
        return
      }

      const externalQuote = isExternalQuoteFlow(customerType, paymentType)

      try {
        if (isCustomOfferFlow) {
          const response = await usersService.verifyEmail({
            email,
            code,
            ...(customerType === CUSTOMER_TYPE.USER || customerType === CUSTOMER_TYPE.ORGANIZATION
              ? { customer_type: customerType }
              : {}),
          })
          if (
            response.access_token &&
            response.refresh_token &&
            response.organization_id !== undefined
          ) {
            authService.storeTokens({
              access: response.access_token,
              refresh: response.refresh_token,
            })
            sessionStorage.setItem('custom_offer_organization_id', response.organization_id)
            window.location.href = ROUTES.DASHBOARD
            return
          }
          setSuccess(true)
        } else if (externalQuote) {
          const verifyResponse = await usersService.verifyOrgEmail({ email, code })
          authService.storeTokens({
            access: verifyResponse.access_token,
            refresh: verifyResponse.refresh_token,
          })

          await organizationsService.createExternalSubscription(verifyResponse.organization_id)

          setSuccess(true)
        } else {
          const response = await usersService.verifyEmail({
            email,
            code,
            ...(customerType === CUSTOMER_TYPE.USER || customerType === CUSTOMER_TYPE.ORGANIZATION
              ? { customer_type: customerType }
              : {}),
            ...(paymentType === PAYMENT_TYPE.INTERNAL ||
            paymentType === PAYMENT_TYPE.EXTERNAL ||
            paymentType === PAYMENT_TYPE.CUSTOM
              ? { payment_type: paymentType as (typeof PAYMENT_TYPE)[keyof typeof PAYMENT_TYPE] }
              : {}),
          })

          if (
            response.access_token &&
            response.refresh_token &&
            response.organization_id !== undefined
          ) {
            authService.storeTokens({
              access: response.access_token,
              refresh: response.refresh_token,
            })
            sessionStorage.setItem('custom_offer_organization_id', response.organization_id)
            window.location.href = ROUTES.DASHBOARD
            return
          }

          setSuccess(true)
          if (response.stripe_payment_url) {
            window.location.href = response.stripe_payment_url
          }
        }
      } catch (err) {
        setError(getApiErrorMessage(err, t('emailVerification.errors.generic')))
      } finally {
        setIsLoading(false)
      }
    }

    verifyEmail()
  }, [email, code, customerType, paymentType, isCustomOfferFlow, t])

  const successTitle = isAdherentFlow
    ? t('emailVerification.adherentSuccessTitle')
    : t('emailVerification.title')
  const successMessage = isAdherentFlow
    ? t('emailVerification.adherentSuccessDescription')
    : isCustomOfferFlow
      ? t('emailVerification.customOfferSuccess')
      : isExternalQuoteFlow(customerType, paymentType)
        ? t('emailVerification.quoteSuccess')
        : t('emailVerification.success')

  return (
    <div className="bg-background flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-8">
      <div className="mx-auto max-w-125">
        <div className="rounded-[26px] border border-[#E9E9E9] bg-white px-13.5 py-10">
          {isLoading && (
            <>
              <div className="mb-4 flex justify-center">
                <Loader2 className="text-primary h-15 w-15 animate-spin" aria-hidden />
              </div>
              <h1 className="text-foreground mb-4 text-center text-xl font-bold">
                {t('emailVerification.title')}
              </h1>
              <p className="text-foreground text-center text-base font-normal">
                {t('emailVerification.subtitle')}
              </p>
            </>
          )}

          {error && (
            <>
              <div className="mb-4 flex justify-center">
                <AlertCircle className="text-destructive h-15 w-15" aria-hidden />
              </div>
              <h1 className="text-foreground mb-4 text-center text-xl font-bold">
                {t('emailVerification.title')}
              </h1>
              <p className="text-foreground text-center text-base font-normal">{error}</p>
            </>
          )}

          {success && (
            <>
              <div className="mb-4 flex justify-center">
                <img src="/check.svg" alt="" width={60} height={60} className="h-15 w-15" />
              </div>
              <h1 className="text-foreground mb-4 text-center text-xl font-bold">{successTitle}</h1>
              <p className="text-foreground text-center text-base font-normal">{successMessage}</p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
